module.exports = { MAX_PUBLIC_SALE_ROWS: 10, canSeeCreatorSale, getPublicOfferDisplayState, getPublicSalePlacementUrl, getPublicSaleRetailerUrl, isSavedSaleReminderEligible };

// Preview audiences restrict early access only; a live public sale is available to every creator.
function canSeeCreatorSale(offer, { now, BrandList_ids = [] }) {
  if (!offer.isPublic || offer.status !== 'active' || offer.publicSaleMetadata?.creatorSalesModeration?.isHidden === true) return false;
  const state = getPublicOfferDisplayState(offer, now);
  if (state === 'live') return true;
  if (state !== 'upcoming') return false;
  if (!offer.creatorVisibleAt || Date.parse(offer.creatorVisibleAt) > now.getTime()) return false;
  const audience = offer.creatorPreviewAudience;
  return audience?.type === 'all' || (audience?.type === 'lists' && audience.BrandList_ids?.some(id => BrandList_ids.includes(id)));
}

// Keep display state consistent with terminal intent and whole-second sale boundaries.
function getPublicOfferDisplayState(offer, now = new Date()) {
  if (offer.status === 'draft') return 'draft';
  if (offer.status === 'ended' || (offer.claimEndsAt && Date.parse(offer.claimEndsAt) <= now.getTime())) return 'ended';
  if (offer.publicSaleMetadata?.sourceAvailability === 'unavailable') return 'unavailable';
  return offer.claimStartsAt && Date.parse(offer.claimStartsAt) > now.getTime() ? 'upcoming' : 'live';
}

// Preserve complete merchant links accepted by public-sale placement validation.
function getPublicSalePlacementUrl(value) {
  if (typeof value !== 'string' || !value || value.length > 2048) return null;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password && url.hostname.includes('.') ? value : null;
  } catch {
    return null;
  }
}

// A retailer name must identify one known domain; partial and ambiguous matches stay unlinked.
function getPublicSaleRetailerUrl(name, merchants) {
  if (typeof name !== 'string' || !name.trim()) return null;
  const domains = merchants.filter(merchant => merchant.name?.trim().toLowerCase() === name.trim().toLowerCase()).map(merchant => {
    const domain = merchant.domain?.toLowerCase().replace(/^www\./, '');
    if (!domain || !getPublicSalePlacementUrl(`https://${domain}`)) return null;
    return new URL(`https://${domain}`).hostname === domain ? domain : null;
  });
  const uniqueDomains = [...new Set(domains)];
  return uniqueDomains.length === 1 && uniqueDomains[0] ? `https://${uniqueDomains[0]}` : null;
}

// A saved stage qualifies before its start and by 9 AM on the creator's start date.
function isSavedSaleReminderEligible({ savedAt, startsAt, timezone }) {
  if (savedAt == null || startsAt == null) return false;
  const saved = new Date(savedAt);
  const start = new Date(startsAt);
  if (!Number.isFinite(saved.getTime()) || !Number.isFinite(start.getTime()) || saved >= start) return false;
  let formatter;
  try {
    formatter = getReminderDateFormatter(timezone || 'America/New_York');
  } catch {
    formatter = getReminderDateFormatter('America/New_York');
  }
  const savedParts = Object.fromEntries(formatter.formatToParts(saved).map(part => [part.type, part.value]));
  const startParts = Object.fromEntries(formatter.formatToParts(start).map(part => [part.type, part.value]));
  const savedDay = `${savedParts.year}-${savedParts.month}-${savedParts.day}`;
  const startDay = `${startParts.year}-${startParts.month}-${startParts.day}`;
  if (savedDay !== startDay) return savedDay < startDay;
  const savedTime = Number(savedParts.hour) * 3600000 + Number(savedParts.minute) * 60000 + Number(savedParts.second) * 1000 + saved.getUTCMilliseconds();
  return savedTime <= 9 * 3600000;
}

const reminderDateFormatters = new Map();
const getReminderDateFormatter = timezone => {
  if (!reminderDateFormatters.has(timezone)) {
    reminderDateFormatters.set(timezone, new Intl.DateTimeFormat('en-US', {
      timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
    }));
  }
  return reminderDateFormatters.get(timezone);
};
